/**
 * Раздел «Календарь» (этап 4) — вызовы GiperCRM. Сверено с кодом CRM:
 *   выдача недели/месяца, быстрые задачи — GiperCRM/server/src/routes/calendar.ts (+ domain/calendarScope.ts, domain/calendarTasks.ts);
 *   встреча с клиентом, личное время, список людей — routes/bookings.ts (+ domain/meetings.ts);
 *   открытые задачи по сегодня («Просрочено», значок) — GET /calendar/tasks/open (routes/calendar.ts, с 11.10.2026);
 *     у старого сервера её нет (404) — тогда «моя сводка» routes/me.ts + services/meSummary.ts;
 *   заготовки быстрых задач — routes/quickTasks.ts; поиск лида — routes/deals.ts (GET /deals?search=).
 */
import { crmFetch } from './crm';
import { ApiError } from '@/lib/http';
import { openFromServer, openFromSummary, type MyOpenTasks } from '@/lib/agenda';

export interface FeedTask {
  id: string;
  title: string;
  entityName: string | null;
  /** Местный день ГГГГ-ММ-ДД (в поясе организации). */
  dueDate: string;
  /** Есть — задача на время; нет — на весь день. */
  startsAt: string | null;
  assigneeUserId: string | null;
  entityType: 'deal' | 'person' | 'company' | null;
  entityId: string | null;
  templateKey: string | null;
  color: string | null;
  remindBeforeMin: number | null;
  repeat: string | null;
  done: boolean;
  /** Закрыта системой — почему («Разговор с клиентом 09.10 14:20, 3 мин»). */
  doneReason?: string | null;
  /** Моя задача (исполнитель — я). */
  own: boolean;
}

export interface FeedMeeting {
  id: string;
  title: string | null;
  guestName: string;
  startsAt: string;
  endsAt: string;
  durationMin: number;
  status: string;
  staffUserId: string | null;
  dealId: string | null;
  deal: { id: string; title: string; isLead: boolean } | null;
  own: boolean;
}

export interface FeedBlock {
  id: string;
  title: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  staffUserId: string | null;
  own: boolean;
  /** Название чужой пометки скрыто настройкой организации — показываем «Занято». */
  titleHidden: boolean;
}

export interface CalendarFeed {
  meetings: FeedMeeting[];
  tasks: FeedTask[];
  blocks: FeedBlock[];
  settings: { defaultMeetingMin: number };
  canSeeOthers: boolean;
  showOtherTitles: boolean;
}

/**
 * GET /api/crm/calendar/feed?from&to&staff — всё для дня/недели/месяца (routes/calendar.ts ~56).
 * Период — не длиннее 62 дней. staff: не задан — своё; all — все; id — человек (403 без права).
 */
export function getFeed(from: string, to: string, staff: string): Promise<CalendarFeed> {
  const p = new URLSearchParams({ from, to });
  if (staff && staff !== 'me') p.set('staff', staff);
  return crmFetch<CalendarFeed>(`/api/crm/calendar/feed?${p.toString()}`);
}

export interface Host { id: string; name: string; position: string | null; me: boolean }

/** GET /api/crm/bookings/hosts — чьи календари можно смотреть (руководителю и админу; иначе 403). */
export async function getHosts(): Promise<Host[]> {
  const r = await crmFetch<{ data: Host[] }>('/api/crm/bookings/hosts');
  return r.data ?? [];
}

export interface TaskPatch {
  title?: string;
  dueDate?: string;
  startsAt?: string | null;
  done?: boolean;
}

/** PATCH /api/crm/calendar/tasks/:id — перенести, отметить сделанной / вернуть в работу (routes/calendar.ts ~262). */
export const patchTask = (id: string, patch: TaskPatch) =>
  crmFetch<FeedTask & { next: FeedTask | null }>(`/api/crm/calendar/tasks/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(patch),
  });

export interface NewCalendarTask {
  title: string;
  dueDate: string;
  startsAt?: string | null;
  remindBeforeMin?: number | null;
  repeat?: string | null;
  assigneeUserId?: string;
  entityType?: 'deal';
  entityId?: string;
  templateKey?: string;
  color?: string | null;
}

/** POST /api/crm/calendar/tasks — быстрая задача (routes/calendar.ts ~243). Коллеге — только руководителю. */
export const createCalendarTask = (t: NewCalendarTask) =>
  crmFetch<FeedTask>('/api/crm/calendar/tasks', { method: 'POST', body: JSON.stringify(t) });

export interface NewMeeting {
  title?: string;
  startsAt: string;
  durationMin: number;
  dealId?: string;
  guestName?: string;
  notifyClient?: boolean;
}

/** POST /api/crm/bookings — встреча, которую заводит сотрудник (routes/bookings.ts ~347, domain/meetings.createMeeting). */
export const createMeeting = (m: NewMeeting) =>
  crmFetch<FeedMeeting>('/api/crm/bookings', { method: 'POST', body: JSON.stringify(m) });

/** POST /api/crm/bookings/blocks — «занять личное время» (routes/bookings.ts ~479). */
export const createBlock = (b: { title?: string; startsAt: string; endsAt: string; allDay?: boolean }) =>
  crmFetch<FeedBlock>('/api/crm/bookings/blocks', { method: 'POST', body: JSON.stringify(b) });

/** Ответ «моей сводки» — только то, что нужно календарю (services/meSummary.ts; поля snake_case — договор с помощником). */
export interface MeSummaryTasks {
  overdue: number;
  due_today: number;
  items: Array<{ id: string; title: string; due_date: string; starts_at: string | null; entity_type: string | null; entity_id: string | null }>;
}

/** Ответ GET /api/crm/calendar/tasks/open (contracts/crm-calendar/openapi.yaml, listOpenQuickTasks). */
export interface OpenTasksResponse {
  data: Array<FeedTask & { bucket: 'overdue' | 'today' | 'ahead' }>;
  until: string;
  today: string;
  total: number;
  overdue: number;
  truncated: boolean;
}

/**
 * Мои открытые задачи по сегодня включительно — «Просрочено» и значок «Календаря».
 *
 * Сначала GET /api/crm/calendar/tasks/open: все (до 500), с названием сделки. Старый сервер её не знает
 * (404) — тогда GET /api/crm/me/summary?period=today (тот же адрес, что у Ассистентуса): там список
 * режется до 20 и без названий, но счётчики точные.
 */
export async function getMyOpenTasks(): Promise<MyOpenTasks> {
  try {
    return openFromServer(await crmFetch<OpenTasksResponse>('/api/crm/calendar/tasks/open'));
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e;
  }
  const r = await crmFetch<{ tasks: MeSummaryTasks }>('/api/crm/me/summary?period=today');
  return openFromSummary(r.tasks ?? { overdue: 0, due_today: 0, items: [] });
}

export interface DealOption {
  id: string;
  title: string;
  isLead: boolean;
  personName: string | null;
}

/** GET /api/crm/deals?search=&limit=8 — выбор лида или сделки (как LeadPicker веб-CRM). */
export async function searchDeals(text: string): Promise<DealOption[]> {
  const p = new URLSearchParams({ search: text, limit: '8' });
  const r = await crmFetch<unknown>(`/api/crm/deals?${p.toString()}`);
  const list = Array.isArray(r) ? r : Array.isArray((r as { data?: unknown })?.data) ? (r as { data: unknown[] }).data : [];
  return (list as Array<Record<string, any>>).map((d) => ({
    id: String(d.id),
    title: String(d.title ?? ''),
    isLead: d.stage?.stageType === 'lead',
    personName: [d.person?.firstName, d.person?.lastName].filter(Boolean).join(' ').trim() || null,
  }));
}
